import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { addonProductsAPI, customersAPI, feedFormulasAPI, salesOrdersAPI, usersAPI, quotesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CustomerCreationModal from '../components/CustomerCreationModal';
import './OrderEntry.css';

interface Customer {
  id: number;
  name: string;
  customer_type: string;
  contact_person: string;
  city: string;
  state: string;
  postal_code: string;
  address: string;
  phone: string;
  email: string;
}

interface User {
  id: number;
  username: string;
  first_name: string;
  role: string;
}

interface FeedFormula {
  id: number;
  name: string;
  description: string;
  feed_grade: string;
  animal_type: string;
  bag_size: number;
  price_bag_mash: number;
  price_bag_pellet: number;
  price_byoc_mash: number;
  price_byoc_pellet: number;
  price_1000lb_mash: number;
  price_1000lb_pellet: number;
  price_2000lb_mash: number;
  price_2000lb_pellet: number;
  pellets_available: boolean;
  default_form: string;
  available_forms: string[];
}

// Max weight one pallet holds — the single frontend source for the pallet math and
// the oversize per-unit warning (mirrors backend MAX_LBS_PER_PALLET).
const PALLET_MAX_LBS = 2600;

// Bags or bulk - the one packaging question staff answer per line. The bag SIZE comes
// from the feed (FeedFormula.bag_size: 40 Five Star, 50 Essentials, 19 Tiny Dino), never
// from the 38-42 lb guessing window this screen used to apply to "pounds / packages".
// Order 2610-0006 (2026-10-12) is why the label can no longer disagree with the numbers.
type Packaging = 'bags' | 'bulk';
const BAGGED_TYPES = ['BAGS', 'BAGS_19'];

const bagSizeOf = (formula: FeedFormula | null): number =>
  formula && Number(formula.bag_size) > 0 ? Number(formula.bag_size) : 40;

const bagWord = (lbs: number) => (lbs === 19 ? 'Tiny Dino 19 lb bag' : `${lbs} lb bag`);

const packagingOf = (item: OrderLineItem): Packaging => item.packaging ?? 'bags';

// A line coming back from the database: bag-shaped is bags (N > 1 packages at bag weight,
// the same test the server applies), otherwise whatever it was saved as.
const inferPackaging = (packageType: string | undefined, lbs: number, packages: number): Packaging => {
  const pt = (packageType || '').toUpperCase();
  if (pt === 'BYOC') return 'bulk';   // the customer's own containers: per pound whatever they weigh
  const perPackage = packages > 0 ? lbs / packages : lbs;
  if (packages > 1 && perPackage > 0 && perPackage <= 50) return 'bags';
  return !pt || BAGGED_TYPES.includes(pt) ? 'bags' : 'bulk';
};

// What the API calls this line. The server re-derives it from the numbers anyway
// (order_service.reconcile_package_type); this says the same thing up front.
const apiPackageType = (item: OrderLineItem): string => {
  if (packagingOf(item) === 'bags') return bagSizeOf(item.formula) === 19 ? 'BAGS_19' : 'BAGS';
  // A line loaded for edit keeps its own bulk flavour (BYOC, SUPERSACK_1000, ...).
  const kept = (item.packageType || '').toUpperCase();
  if (kept && !BAGGED_TYPES.includes(kept) && kept !== 'BULK') return kept;
  const perPackage = item.numberOfPackages > 0 ? item.totalPounds / item.numberOfPackages : item.totalPounds;
  return perPackage >= 1000 ? 'SUPERSACK' : 'BULK';
};

const previewPackageType = (item: OrderLineItem): string => {
  if (packagingOf(item) === 'bags') return bagSizeOf(item.formula) === 19 ? 'bags_19' : 'bags';
  if ((item.packageType || '').toUpperCase() === 'BYOC') return 'byoc';
  const perPackage = item.numberOfPackages > 0 ? item.totalPounds / item.numberOfPackages : item.totalPounds;
  return perPackage >= 1000 && perPackage <= 2000 ? 'supersack' : 'bulk';
};

// Bags: whole bags of the formula's size - a weight rounds UP to the next bag.
const asBags = (item: OrderLineItem, pounds: number, bags?: number): OrderLineItem => {
  const size = bagSizeOf(item.formula);
  const count = bags !== undefined ? Math.max(0, Math.floor(bags))
    : pounds > 0 ? Math.ceil(pounds / size) : 0;
  return { ...item, packaging: 'bags', packageSizeLbs: size, numberOfPackages: count, totalPounds: count * size };
};

// Bulk: the weight as typed, split across N super sacks.
const asBulk = (item: OrderLineItem, pounds: number, sacks: number): OrderLineItem => {
  const count = Math.max(1, Math.floor(sacks) || 1);
  return { ...item, packaging: 'bulk', numberOfPackages: count, totalPounds: pounds, packageSizeLbs: pounds > 0 ? Math.round(pounds / count) : 0 };
};

interface OrderLineItem {
  id: string; // Temporary ID for tracking
  formula: FeedFormula | null;
  selectedGrade: string;
  selectedAnimal: string;
  isPellet: boolean;
  totalPounds: number;
  numberOfPackages: number; // bags: how many bags. bulk: how many super sacks.
  packageSizeLbs: number;   // bags: the bag weight. bulk: pounds per sack.
  packaging?: Packaging;    // absent on a line not yet touched -> bags
  packageType?: string; // Original package type from DB (keeps a bulk line's BYOC / SUPERSACK_* flavour on edit)
  isPromo: boolean;
  promoReason: string;
  // API pricing response
  calculatedPrice?: {
    item_total: number;
    price_per_lb?: number;
    price_per_package?: number;
    pricing_display_method?: string;
    unit_price?: number;
  };
}

// A catalogue item offered in the Add-ons section: bulk ingredient, canola
// oil, grit. `priced_by_weight` is the SERVER's own pricing decision, echoed
// back so the form never has to guess which way a line will be priced.
interface AddonProduct {
  id: number;
  name: string;
  sku: string;
  unit: string;
  default_price: string;
  priced_by_weight: boolean;
  addon_weight_lbs: string | null;
}

interface AddonLine {
  id: string;
  productId: number | null;
  // Pounds for a weight item, count for an item sold each. One field, because
  // which one it means is decided by the product, not by the person typing.
  quantity: number;
}

interface MaterialCheck {
  formula_name: string;
  quantity_lbs: number;
  all_materials_available: boolean;
  materials: Array<{
    material_name: string;
    required_qty: number;
    available_qty: number;
    unit: string;
    is_available: boolean;
    shortage: number;
  }>;
}

interface Quote {
  id: number;
  quote_number: string;
  customer: number;
  customer_name: string;
  quote_date: string;
  expiration_date: string;
  status: string;
  shipping_method: string;
  is_rush: boolean;
  is_tax_exempt: boolean;
  total_weight_lbs: number;
  estimated_pallets: number;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  special_instructions: string;
  notes: string;
  entered_by: number;
  entered_by_name: string;
  converted_to_order: number | null;
  converted_at: string | null;
  items: Array<{
    id: number;
    formula: number;
    formula_name: string;
    package_type: string;
    is_pelleted: boolean;
    total_quantity_lbs: number;
    number_of_packages: number;
    package_size_lbs: number;
    unit_price_per_lb: number;
    discount: number;
    line_total: number;
    notes: string;
  }>;
}

// Primary shipping methods — matches wholesale portal
const SHIPPING_METHODS = [
  { value: 'WILL_CALL_KF', label: 'Will Call (Pickup at Mill)' },
  { value: 'LTL_WILL_CALL', label: 'Terminal Pickup', isLTL: true },
  { value: 'LTL_STORE', label: 'Ship to Store', isLTL: true },
  { value: 'LTL_FARM', label: 'Ship to Farm', isLTL: true },
  { value: 'MILK_RUN', label: 'Milk Run' },
  // Legacy methods — kept for backward compatibility with old orders
  { value: 'WILL_CALL', label: 'Will Call (Legacy)' },
  { value: 'LTL_DOCK', label: 'LTL Ship to Dock (Legacy)', isLTL: true },
  { value: 'LTL_BUSINESS', label: 'LTL Ship to Business (Legacy)', isLTL: true },
  { value: 'OAK_HARBOR', label: 'Oak Harbor (Legacy)' },
  { value: 'DIAMOND_LINE', label: 'Diamond Line (Legacy)' },
];

// Reverse map: display label → value key (handles DB records stored with display labels)
const SHIPPING_LABEL_TO_VALUE: Record<string, string> = {};
SHIPPING_METHODS.forEach((m) => { SHIPPING_LABEL_TO_VALUE[m.label] = m.value; });

const resolveShippingMethod = (raw: string): string => {
  if (!raw) return 'WILL_CALL';
  // Already a valid key?
  if (SHIPPING_METHODS.some((m) => m.value === raw)) return raw;
  // Try matching as a display label
  return SHIPPING_LABEL_TO_VALUE[raw] || 'WILL_CALL';
};

const NON_EDITABLE_STATUSES = ['IN_PRODUCTION', 'PRODUCTION', 'MILLED', 'PACKAGING', 'READY', 'READY_TO_SHIP', 'SHIPPED', 'DELIVERED'];

const OrderEntry: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const editOrderId = searchParams.get('edit');

  // State management
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const [employees, setEmployees] = useState<User[]>([]);
  const [enteredBy, setEnteredBy] = useState<number | null>(null);

  // Entry Type: ORDER or QUOTE
  const [entryType, setEntryType] = useState<'ORDER' | 'QUOTE'>('ORDER');

  // Quote-specific state
  const [savedQuotes, setSavedQuotes] = useState<Quote[]>([]);
  const [loadedQuote, setLoadedQuote] = useState<Quote | null>(null);
  const [showQuotesModal, setShowQuotesModal] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');

  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryWarning, setDeliveryWarning] = useState('');
  const [deliveryScheduleInfo, setDeliveryScheduleInfo] = useState<any>(null);
  const [validDeliveryDates, setValidDeliveryDates] = useState<string[]>([]);
  const [shippingMethod, setShippingMethod] = useState('WILL_CALL');
  const [shippingProfile, setShippingProfile] = useState<any>(null);
  const [deliveryValidation, setDeliveryValidation] = useState<any>(null);
  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [isRush, setIsRush] = useState(false);
  // null = required explicit choice not yet made on a delivery method. Never false by default.
  const [liftgateRequired, setLiftgateRequired] = useState<boolean | null>(null);
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOrderNumber, setEditingOrderNumber] = useState('');
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [editReason, setEditReason] = useState('');
  const originalOrderDataRef = useRef<any>(null);

  // Multiple line items
  // The multi-pallet bag discount the quote applied (already in the line prices and subtotal).
  const [palletDiscount, setPalletDiscount] = useState<{ pallets: number; per_bag: number; bags: number; savings: number } | null>(null);
  const [lineItems, setLineItems] = useState<OrderLineItem[]>([
    {
      id: crypto.randomUUID(),
      formula: null,
      selectedGrade: '',
      selectedAnimal: '',
      isPellet: false,
      totalPounds: 0,
      numberOfPackages: 1,
      packageSizeLbs: 0,
      isPromo: false,
      promoReason: '',
    },
  ]);

  // Add-ons: non-feed lines (bulk ingredients, canola oil, grit)
  const [addonProducts, setAddonProducts] = useState<AddonProduct[]>([]);
  const [addonLines, setAddonLines] = useState<AddonLine[]>([]);
  // Server-priced add-on totals, keyed by add-on line id. The browser could
  // multiply price x quantity itself, but toFixed rounds a half-cent away
  // from zero while the server's Decimal.quantize rounds half to even, so a
  // locally computed preview can disagree with the invoice by a penny.
  const [addonPrices, setAddonPrices] = useState<Map<string, number>>(new Map());

  const [feedGrades, setFeedGrades] = useState<Array<{ value: string; label: string }>>([]);
  const [materialChecks, setMaterialChecks] = useState<Map<string, MaterialCheck>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ref to track last pricing request to avoid duplicate API calls
  const lastPriceRequestRef = useRef<string>('');

  // Load customers on mount
  useEffect(() => {
    loadCustomers();
    loadFeedGrades();
    loadEmployees();
    loadAddonProducts();
  }, []);

  // Default "Entered By" to the logged-in staff member from the cookie-backed
  // session (AuthContext). This previously read localStorage.getItem('user'),
  // a key the cookie-only auth flow no longer writes, so the default silently
  // never applied. Guards: only for NEW order entry (edit mode loads the order's
  // own entered_by), only once employees are loaded, and only while no selection
  // exists yet — so it never clobbers a loaded order or a manual pick.
  useEffect(() => {
    if (isEditMode) return;
    if (enteredBy !== null) return;
    if (!authUser || employees.length === 0) return;
    if (employees.some((emp) => emp.id === authUser.id)) {
      setEnteredBy(authUser.id);
    }
  }, [authUser, employees, isEditMode, enteredBy]);

  // Load customer quotes when customer changes in quote mode
  useEffect(() => {
    if (selectedCustomer && entryType === 'QUOTE') {
      loadCustomerQuotes(selectedCustomer.id);
    } else {
      setSavedQuotes([]);
    }
  }, [selectedCustomer, entryType]);

  const loadEmployees = async () => {
    try {
      const response = await usersAPI.getEmployees();
      const employeeData = response.data;
      setEmployees(employeeData);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  // Load order for editing when edit param present
  const loadOrderForEdit = async (orderId: string, customerList: Customer[]) => {
    setLoadingOrder(true);
    try {
      const response = await salesOrdersAPI.getById(parseInt(orderId));
      const order = response.data;

      // Block if status is non-editable
      if (NON_EDITABLE_STATUSES.includes(order.status)) {
        setError(`Cannot edit order in ${order.status} status.`);
        setLoadingOrder(false);
        return;
      }

      setIsEditMode(true);
      setEditingOrderNumber(order.order_number);

      // Set customer
      const customer = customerList.find((c: Customer) => c.id === order.customer) || null;
      setSelectedCustomer(customer);

      // Set order fields
      setDeliveryDate(order.delivery_date || '');
      setShippingMethod(resolveShippingMethod(order.shipping_method));
      setIsTaxExempt(order.is_tax_exempt || false);
      setIsRush(order.is_rush || false);
      setLiftgateRequired(order.liftgate_required || false);
      setSpecialInstructions(order.special_instructions || '');
      setEnteredBy(order.entered_by || null);

      // Map order items to line items
      const mappedItems: OrderLineItem[] = await Promise.all(
        order.items.map(async (item: any) => {
          const lineId = crypto.randomUUID();
          let formula: FeedFormula | null = null;
          const grade = item.feed_grade || '';
          const animal = item.animal_type || '';

          // Load available formulas for this grade/animal combo
          if (grade && animal) {
            try {
              const formulaResp = await feedFormulasAPI.getFilterOptions({
                feed_grade: grade,
                animal_type: animal,
              });
              const formulas = formulaResp.data.formulas || [];
              formula = formulas.find((f: FeedFormula) => f.id === item.formula) || null;
            } catch (err) {
              console.error('Error loading formula for edit:', err);
            }
          }

          return {
            id: lineId,
            formula,
            selectedGrade: grade,
            selectedAnimal: animal,
            isPellet: item.is_pellet ?? item.is_pelleted ?? false,
            totalPounds: parseFloat(item.total_quantity_lbs) || 0,
            numberOfPackages: item.number_of_packages || 1,
            packageSizeLbs: parseFloat(item.package_size_lbs) || 0,
            packageType: item.package_type || 'BAGS',
            packaging: inferPackaging(
              item.package_type, parseFloat(item.total_quantity_lbs) || 0, item.number_of_packages || 1,
            ),
            isPromo: item.is_promo || false,
            promoReason: item.promo_reason || '',
          };
        })
      );

      setLineItems(mappedItems.length > 0 ? mappedItems : [{
        id: crypto.randomUUID(),
        formula: null,
        selectedGrade: '',
        selectedAnimal: '',
        isPellet: false,
        totalPounds: 0,
        numberOfPackages: 1,
        packageSizeLbs: 0,
        isPromo: false,
        promoReason: '',
      }]);

      // Store original data for change detection
      originalOrderDataRef.current = {
        customer: order.customer,
        delivery_date: order.delivery_date,
        shipping_method: order.shipping_method,
        is_rush: order.is_rush,
        is_tax_exempt: order.is_tax_exempt,
        liftgate_required: order.liftgate_required,
        special_instructions: order.special_instructions || '',
        items: order.items,
      };
    } catch (err: any) {
      console.error('Error loading order for edit:', err);
      setError('Failed to load order for editing.');
    } finally {
      setLoadingOrder(false);
    }
  };

  // Trigger edit mode when edit param is present and customers are loaded
  useEffect(() => {
    if (editOrderId && customers.length > 0) {
      loadOrderForEdit(editOrderId, customers);
    }
  }, [editOrderId, customers]);

  // Auto-load shipping profile (always) + liftgate/delivery schedule defaults (new orders only)
  useEffect(() => {
    if (!selectedCustomer) return;

    // Always load shipping profile (read-only context for admin)
    const loadShippingProfile = async () => {
      try {
        const profileResp = await customersAPI.getShippingProfile(selectedCustomer.id);
        setShippingProfile(profileResp.data);
      } catch {
        setShippingProfile(null);
      }
    };
    loadShippingProfile();

    // Only set defaults for new orders (don't overwrite existing order values)
    if (!isEditMode) {
      const loadCustomerDefaults = async () => {
        try {
          const resp = await customersAPI.getDeliveryInfo(selectedCustomer.id);
          if (resp.data && resp.data.requires_liftgate) {
            setLiftgateRequired(true);
          }
        } catch {
          // No delivery info - that's fine
        }
        // Load valid delivery dates for carrier schedule
        try {
          const datesResp = await customersAPI.getValidDeliveryDates(selectedCustomer.id);
          if (datesResp.data) {
            setDeliveryScheduleInfo(datesResp.data.schedule_info);
            setValidDeliveryDates(
              (datesResp.data.valid_dates || []).map((d: any) => d.date)
            );
          }
        } catch {
          setDeliveryScheduleInfo(null);
          setValidDeliveryDates([]);
        }
      };
      loadCustomerDefaults();
    }
  }, [selectedCustomer, isEditMode]);

  // Derive whether current shipping method supports liftgate (any delivery method, not pickup/store)
  const isDeliveryMethod = shippingMethod
    && !shippingMethod.includes('WILL_CALL')
    && !shippingMethod.includes('PICKUP')
    && shippingMethod !== 'STORE';

  // Reset liftgate when shipping method changes to pickup/store
  useEffect(() => {
    if (!isDeliveryMethod) {
      setLiftgateRequired(null);
    }
  }, [shippingMethod, isDeliveryMethod]);

  // Debounced pricing recalculation when line items change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculatePricing();
    }, 500); // 500ms debounce to wait for user to finish typing

    return () => clearTimeout(timeoutId); // Cleanup previous timeout
  }, [selectedCustomer, lineItems]);

  const loadCustomers = async () => {
    try {
      const response = await customersAPI.getAll({ paginate: false, is_active: true });
      // With paginate=false, API returns a flat array
      const customerData = Array.isArray(response.data) ? response.data : (response.data.results || response.data);
      setCustomers(customerData.filter((c: Customer) => c.id));
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers. Please refresh the page.');
    }
  };

  const loadFeedGrades = async () => {
    try {
      const response = await feedFormulasAPI.getFilterOptions({});
      const grades = response.data.grades || [];

      // Sort grades in correct order: Essential → Plus → Five Star
      const gradeOrder = ['ESSENTIAL', 'PLUS', 'FIVE_STAR'];
      const sortedGrades = grades.sort((a: any, b: any) => {
        const aIndex = gradeOrder.indexOf(a.value);
        const bIndex = gradeOrder.indexOf(b.value);
        return aIndex - bIndex;
      });

      setFeedGrades(sortedGrades);
    } catch (err) {
      console.error('Error loading feed grades:', err);
    }
  };

  // Load animal types when grade is selected
  const loadAnimalTypes = async (lineItemId: string, grade: string) => {
    try {
      const response = await feedFormulasAPI.getFilterOptions({ feed_grade: grade });
      return response.data.animal_types || [];
    } catch (err) {
      console.error('Error loading animal types:', err);
      return [];
    }
  };

  // Load formulas when grade and animal are selected
  const loadFormulas = async (lineItemId: string, grade: string, animal: string) => {
    try {
      const response = await feedFormulasAPI.getFilterOptions({
        feed_grade: grade,
        animal_type: animal,
      });
      return response.data.formulas || [];
    } catch (err) {
      console.error('Error loading formulas:', err);
      return [];
    }
  };

  // Handle grade selection
  const handleGradeChange = async (lineItemId: string, grade: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? {
              ...item,
              selectedGrade: grade,
              selectedAnimal: '',
              formula: null,
              isPellet: false,
            }
          : item
      )
    );
  };

  // Handle animal selection
  const handleAnimalChange = async (lineItemId: string, animal: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? { ...item, selectedAnimal: animal, formula: null, isPellet: false }
          : item
      )
    );
  };

  // Handle formula selection
  const handleFormulaChange = async (lineItemId: string, formulaId: number, formulas: FeedFormula[]) => {
    const formula = formulas.find((f) => f.id === formulaId) || null;

    // Set default form based on formula's default_form field
    let defaultIsPellet = false;
    if (formula && formula.default_form) {
      defaultIsPellet = formula.default_form === 'PELLET';
    }

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        const next = { ...item, formula, isPellet: defaultIsPellet };
        // A different feed can mean a different bag (50 lb Essentials vs 40 lb Five
        // Star): keep the bag COUNT and let the weight follow the new bag size.
        return packagingOf(next) === 'bags' && next.totalPounds > 0
          ? asBags(next, 0, next.numberOfPackages)
          : next;
      })
    );

    // Check materials for this formula
    if (formula && lineItems.find((item) => item.id === lineItemId)?.totalPounds) {
      checkMaterials(lineItemId, formula.id, lineItems.find((item) => item.id === lineItemId)!.totalPounds);
    }

    // Note: Pricing recalculation now handled by useEffect below
  };

  // Handle pellet toggle
  const handlePelletToggle = (lineItemId: string, isPellet: boolean) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === lineItemId ? { ...item, isPellet } : item))
    );
    // Note: Pricing recalculation now handled by useEffect below
  };

  // Bags <-> bulk. The numbers are re-derived on every switch so a stale count can
  // never ride along under the other label (the 2610-0006 failure).
  const handlePackagingChange = (lineItemId: string, packaging: Packaging) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId || packagingOf(item) === packaging) return item;
        return packaging === 'bags'
          ? asBags(item, item.totalPounds)        // 2,600 lb of a 40 lb bag feed -> 65 bags
          : asBulk(item, item.totalPounds, 1);    // the same weight, one super sack
      })
    );
  };

  const handlePromoChange = (lineItemId: string, isPromo: boolean) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === lineItemId ? { ...item, isPromo, promoReason: isPromo ? item.promoReason : '' } : item))
    );
  };

  const handlePromoReasonChange = (lineItemId: string, promoReason: string) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === lineItemId ? { ...item, promoReason } : item))
    );
  };

  // Just update the value - NO calculations during typing
  const handleTotalPoundsChange = (lineItemId: string, totalPounds: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, totalPounds } : item
      )
    );
  };

  // When the weight is done being typed: bags round up to whole bags of the feed's
  // size; bulk keeps the weight and re-splits it across the sacks already chosen.
  const handleTotalPoundsBlur = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        return packagingOf(item) === 'bulk'
          ? asBulk(item, item.totalPounds, item.numberOfPackages)
          : asBags(item, item.totalPounds);
      })
    );

    // Check materials with adjusted total
    const updatedItem = lineItems.find((li) => li.id === lineItemId);
    if (updatedItem?.formula && updatedItem.totalPounds > 0) {
      checkMaterials(lineItemId, updatedItem.formula.id, updatedItem.totalPounds);
    }
  };

  // Just update the value - NO calculations during typing
  const handleNumberOfPackagesChange = (lineItemId: string, numberOfPackages: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, numberOfPackages } : item
      )
    );
  };

  // When the count is done being typed: bags -> the weight follows the count; bulk ->
  // the weight stays and the per-sack figure follows the sack count.
  const handleNumberOfPackagesBlur = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        return packagingOf(item) === 'bulk'
          ? asBulk(item, item.totalPounds, item.numberOfPackages)
          : asBags(item, 0, item.numberOfPackages);
      })
    );
  };

  // Check materials availability
  const checkMaterials = async (lineItemId: string, formulaId: number, quantityLbs: number) => {
    if (quantityLbs <= 0) return;

    try {
      const response = await salesOrdersAPI.checkMaterials({
        formula_id: formulaId,
        quantity_lbs: quantityLbs,
      });
      setMaterialChecks((prev) => new Map(prev).set(lineItemId, response.data));
    } catch (err) {
      console.error('Error checking materials:', err);
    }
  };

  // Calculate pricing using API (Stable Cost formulas)
  const calculatePricing = useCallback(async () => {
    if (!selectedCustomer) return;

    const validItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    const addonItems = addonPayload();
    if (validItems.length === 0 && addonItems.length === 0) {
      // Clear pricing for invalid items
      setLineItems((prev) =>
        prev.map((item) => ({
          ...item,
          calculatedPrice: undefined,
        }))
      );
      setAddonPrices(new Map());
      lastPriceRequestRef.current = '';
      return;
    }

    // Create request key to avoid duplicate API calls
    const requestKey = `${selectedCustomer.id}|` + validItems.map((item) =>
      `${item.formula!.id}-${item.isPellet}-${item.totalPounds}-${item.numberOfPackages}-${item.packageSizeLbs}`
    ).join('|')
      + '|addons:' + validAddonLines.map((l) => `${l.productId}-${l.quantity}`).join(',');

    if (requestKey === lastPriceRequestRef.current) {
      return; // Skip - same request as last time
    }
    lastPriceRequestRef.current = requestKey;

    try {
      const payload = {
        customer_id: selectedCustomer.id,
        items: validItems.map((item) => ({
          formula_id: item.formula!.id,
          is_pellet: item.isPellet,
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          package_type: previewPackageType(item),
        })).concat(addonItems as any[]),
      };

      console.log('=== CALCULATE PRICE REQUEST ===');
      console.log('Payload:', JSON.stringify(payload, null, 2));
      console.log('Line items:', validItems.map(item => ({
        name: item.formula?.name,
        totalPounds: item.totalPounds,
        isPellet: item.isPellet
      })));

      const response = await salesOrdersAPI.calculatePrice(payload);

      console.log('=== CALCULATE PRICE RESPONSE ===');
      console.log('Response:', JSON.stringify(response.data, null, 2));
      console.log('Items:', response.data.items.map((item: any) => ({
        formula: item.formula_name,
        qty_lbs: item.total_quantity_lbs,
        price_per_lb: item.price_per_lb,
        item_total: item.item_total,
        calculation: `${item.price_per_lb} × ${item.total_quantity_lbs} = ${item.item_total}`
      })));

      // Update line items with calculated pricing
      // Build a map from line item ID → priced result (index-based, since
      // validItems and response.data.items are in the same order)
      const pricedItems = response.data.items;
      setPalletDiscount(response.data.pallet_discount || null);
      const priceByItemId = new Map<string, any>();
      validItems.forEach((vi, idx) => {
        if (pricedItems[idx]) {
          priceByItemId.set(vi.id, pricedItems[idx]);
        }
      });

      setLineItems((prev) =>
        prev.map((item) => {
          const pricedItem = priceByItemId.get(item.id);
          if (pricedItem) {
            return {
              ...item,
              calculatedPrice: {
                item_total: pricedItem.item_total,
                price_per_lb: pricedItem.price_per_lb,
                price_per_package: pricedItem.price_per_package,
                pricing_display_method: pricedItem.pricing_display_method,
                unit_price: pricedItem.unit_price,
              },
            };
          }
          return { ...item, calculatedPrice: undefined };
        })
      );

      // Add-ons were appended after the feed items, so they start at that
      // offset. Guard on is_addon rather than trusting the offset blindly:
      // if the server ever skips a line the totals go blank instead of
      // attaching one line's price to another line.
      const addonPriceMap = new Map<string, number>();
      validAddonLines.forEach((line, idx) => {
        const priced = pricedItems[validItems.length + idx];
        if (priced && priced.is_addon) {
          addonPriceMap.set(line.id, priced.item_total);
        }
      });
      setAddonPrices(addonPriceMap);
    } catch (err) {
      console.error('Error calculating pricing:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, lineItems, addonLines, addonProducts]); // recalculate when customer, line items or add-ons change

  // ── Add-ons ────────────────────────────────────────────────────────────
  // Nothing appears here until someone ticks "sellable as add-on" on the
  // product, so the picker stays short and deliberate rather than listing
  // the whole QuickBooks catalogue.
  const loadAddonProducts = async () => {
    try {
      const response = await addonProductsAPI.getAll();
      setAddonProducts(response.data || []);
    } catch (err) {
      // A catalogue that will not load must not take the order form down
      // with it: feed entry still works, the Add-ons section just stays empty.
      console.error('Error loading add-on products:', err);
    }
  };

  const addAddonLine = () => {
    setAddonLines((prev) => [
      ...prev,
      { id: crypto.randomUUID(), productId: null, quantity: 0 },
    ]);
  };

  const removeAddonLine = (id: string) => {
    setAddonLines((prev) => prev.filter((line) => line.id !== id));
  };

  const handleAddonProductChange = (id: string, productId: number | null) => {
    // Quantity resets with the product: 500 meaning pounds of oil must not
    // silently become 500 bags of grit.
    setAddonLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, productId, quantity: 0 } : line))
    );
  };

  const handleAddonQuantityChange = (id: string, quantity: number) => {
    setAddonLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, quantity } : line))
    );
  };

  const addonProductById = (productId: number | null) =>
    addonProducts.find((p) => p.id === productId) || null;

  // Lines complete enough to price or send.
  const validAddonLines = addonLines.filter(
    (line) => line.productId !== null && line.quantity > 0
  );

  // The payload shape the server expects. A weight item sends pounds, an item
  // sends a count — matching price_addon_line() on the backend.
  const addonPayload = () =>
    validAddonLines.map((line) => {
      const product = addonProductById(line.productId);
      return product && product.priced_by_weight
        ? { product: line.productId, total_quantity_lbs: line.quantity }
        : { product: line.productId, number_of_packages: line.quantity };
    });

  const addonLineTotal = (line: AddonLine) => {
    const priced = addonPrices.get(line.id);
    if (priced !== undefined) return priced;
    // Fallback only until the preview returns (or if it fails): the server
    // is still what prices the saved order.
    const product = addonProductById(line.productId);
    if (!product || !line.quantity) return 0;
    return parseFloat(product.default_price || '0') * line.quantity;
  };

  // Add new line item
  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        formula: null,
        selectedGrade: '',
        selectedAnimal: '',
        isPellet: false,
        totalPounds: 0,
        numberOfPackages: 1,
        packageSizeLbs: 0,
        isPromo: false,
        promoReason: '',
      },
    ]);
  };

  // Remove line item
  const removeLineItem = (lineItemId: string) => {
    if (lineItems.length === 1) {
      setError('You must have at least one line item');
      return;
    }
    setLineItems((prev) => prev.filter((item) => item.id !== lineItemId));
    setMaterialChecks((prev) => {
      const newMap = new Map(prev);
      newMap.delete(lineItemId);
      return newMap;
    });
  };

  // Calculate order totals using API pricing
  const calculateOrderTotals = () => {
    let totalWeight = 0;
    let subtotal = 0;

    lineItems.forEach((item) => {
      if (!item.formula || item.totalPounds <= 0) return;

      totalWeight += item.totalPounds;

      // Promo items are $0, otherwise use API calculated pricing
      if (item.isPromo) {
        // Promo: weight counts but price is zero
      } else if (item.calculatedPrice) {
        subtotal += item.calculatedPrice.item_total;
      }
    });

    // Add-ons count toward both the money and the weight. Oil is priced by
    // the pound so its weight IS its quantity; an item add-on contributes its
    // catalogue weight, matching what the server will store on the line.
    validAddonLines.forEach((line) => {
      subtotal += addonLineTotal(line);
      const product = addonProductById(line.productId);
      if (!product) return;
      if (product.priced_by_weight) {
        totalWeight += line.quantity;
      } else if (product.addon_weight_lbs) {
        totalWeight += parseFloat(product.addon_weight_lbs) * line.quantity;
      }
    });

    // FIX 11: Calculate pallets with 2600 lb max per pallet
    const estimatedPallets = totalWeight > 0 ? Math.ceil(totalWeight / PALLET_MAX_LBS) : 0;

    // Tax rate: 8.0% Stevens County, WA
    const taxRate = 0.08;
    const taxAmount = isTaxExempt ? 0 : subtotal * taxRate;
    const total = subtotal + taxAmount;

    return {
      totalWeight,
      subtotal,
      taxRate: taxRate * 100,
      taxAmount,
      total,
      estimatedPallets,
    };
  };

  // Submit order
  const handleSubmitOrder = async () => {
    setError('');

    // Validation
    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }

    if (!deliveryDate) {
      setError('Please select a delivery date');
      return;
    }

    if (!enteredBy) {
      setError('Please select who is entering this order');
      return;
    }

    // Liftgate is a required explicit choice on any delivery method — never leave it unset.
    if (isDeliveryMethod && liftgateRequired === null) {
      setError('Please choose whether a liftgate is required for this delivery');
      return;
    }

    const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    // An order may be add-ons only — a drum of oil with no feed on it.
    if (validLineItems.length === 0 && validAddonLines.length === 0) {
      setError('Please add at least one feed line item or add-on');
      return;
    }

    const incompleteAddon = addonLines.find(
      (line) => (line.productId === null) !== (line.quantity <= 0)
    );
    if (incompleteAddon) {
      setError('Every add-on needs both an item and a quantity');
      return;
    }

    const promoMissingReason = validLineItems.find(item => item.isPromo && !item.promoReason.trim());
    if (promoMissingReason) {
      setError('Please provide a reason for all promotional items');
      return;
    }

    setLoading(true);

    const submitOrder = async (skipDeliveryValidation = false) => {
      // Create order with line items
      const orderData: any = {
        customer: selectedCustomer.id,
        order_date: new Date().toISOString().split('T')[0],
        delivery_date: deliveryDate,
        shipping_method: shippingMethod,
        is_rush: isRush,
        is_tax_exempt: isTaxExempt,
        // Coerce to boolean: the submit guard forced an explicit choice on delivery
        // methods; non-delivery methods don't take liftgate, so send false.
        liftgate_required: liftgateRequired === true,
        special_instructions: specialInstructions,
        status: 'PENDING',
        entered_by: enteredBy,
        entered_at: new Date().toISOString(),
        items: validLineItems.map((item) => ({
          formula: item.formula!.id,
          is_pellet: item.isPellet,
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          package_type: apiPackageType(item),
          is_promo: item.isPromo || false,
          promo_reason: item.promoReason || '',
        })).concat(addonPayload() as any[]),
      };

      if (skipDeliveryValidation) {
        orderData.skip_delivery_validation = true;
      }

      return salesOrdersAPI.create(orderData);
    };

    try {
      const response = await submitOrder();

      // Show delivery warnings if any
      if (response.data?.delivery_warnings?.length > 0) {
        alert('Order created.\n\n⚠ Delivery warnings:\n' + response.data.delivery_warnings.join('\n'));
      } else {
        alert('Order created successfully!');
      }

      // Reset form
      setLineItems([
        {
          id: crypto.randomUUID(),
          formula: null,
          selectedGrade: '',
          selectedAnimal: '',
          isPellet: false,
          totalPounds: 0,
          numberOfPackages: 1,
          packageSizeLbs: 0,
          isPromo: false,
          promoReason: '',
        },
      ]);
      setDeliveryDate('');
      setShippingMethod('WILL_CALL');
      setIsTaxExempt(false);
      setIsRush(false);
      setSpecialInstructions('');
      setMaterialChecks(new Map());
    } catch (err: any) {
      console.error('Error creating order:', err);
      const errorData = err.response?.data;

      // Handle delivery validation errors with admin override option
      if (errorData?.delivery_errors) {
        const msg = errorData.delivery_errors.join('\n');
        const override = window.confirm(
          `⚠ Delivery Date Problem:\n\n${msg}\n\nAs admin, do you want to override and save anyway?`
        );
        if (override) {
          try {
            const retryResponse = await submitOrder(true);
            if (retryResponse.data?.delivery_warnings?.length > 0) {
              alert('Order created (override).\n\n⚠ Delivery warnings:\n' + retryResponse.data.delivery_warnings.join('\n'));
            } else {
              alert('Order created successfully (delivery validation overridden).');
            }
            setDeliveryDate('');
            setShippingMethod('WILL_CALL');
            setIsTaxExempt(false);
            setIsRush(false);
            setSpecialInstructions('');
            setMaterialChecks(new Map());
            setLineItems([{
              id: crypto.randomUUID(), formula: null, selectedGrade: '', selectedAnimal: '',
              isPellet: false, totalPounds: 0, numberOfPackages: 1, packageSizeLbs: 0,
              isPromo: false, promoReason: '',
            }]);
          } catch (retryErr: any) {
            setError(retryErr.response?.data?.message || 'Failed to create order');
          }
          return;
        }
        setError(msg);
        return;
      }

      // Standard error handling
      let errorMessage = 'Failed to create order';
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.details) {
          const details = Object.entries(errorData.details)
            .map(([field, errors]: [string, any]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          errorMessage = details || errorMessage;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle update order (edit mode)
  const handleUpdateOrder = async () => {
    setError('');

    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }
    if (!deliveryDate) {
      setError('Please select a delivery date');
      return;
    }

    const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    if (validLineItems.length === 0 && validAddonLines.length === 0) {
      setError('Please add at least one feed line item or add-on');
      return;
    }

    // Show edit reason modal
    setShowEditReasonModal(true);
  };

  const submitOrderUpdate = async () => {
    if (!editReason.trim()) {
      setError('Please provide a reason for the edit');
      return;
    }

    setShowEditReasonModal(false);
    setLoading(true);
    try {
      const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
      const updateData = {
        customer: selectedCustomer!.id,
        delivery_date: deliveryDate,
        shipping_method: shippingMethod,
        is_rush: isRush,
        is_tax_exempt: isTaxExempt,
        liftgate_required: liftgateRequired === true,
        special_instructions: specialInstructions,
        entered_by: enteredBy,
        edit_reason: editReason,
        items: validLineItems.map((item) => ({
          formula: item.formula!.id,
          is_pellet: item.isPellet,
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          package_type: apiPackageType(item),
          is_promo: item.isPromo || false,
          promo_reason: item.promoReason || '',
        })).concat(addonPayload() as any[]),
      };

      await salesOrdersAPI.patch(parseInt(editOrderId!), updateData);
      alert(`Order ${editingOrderNumber} updated successfully!`);
      navigate(`/orders/${editOrderId}`);
    } catch (err: any) {
      console.error('Error updating order:', err);
      const errorData = err.response?.data;
      setError(errorData?.message || errorData?.error || 'Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  // Load customer quotes
  const loadCustomerQuotes = async (customerId: number) => {
    try {
      const response = await quotesAPI.getByCustomer(customerId);
      setSavedQuotes(response.data);
    } catch (err) {
      console.error('Error loading customer quotes:', err);
    }
  };

  // Load a quote into the form
  const loadQuote = (quote: Quote) => {
    setLoadedQuote(quote);
    setShowQuotesModal(false);

    // Populate form with quote data
    setExpirationDate(quote.expiration_date);
    setShippingMethod(resolveShippingMethod(quote.shipping_method));
    setIsTaxExempt(quote.is_tax_exempt);
    setIsRush(quote.is_rush);
    setSpecialInstructions(quote.special_instructions);
    setEnteredBy(quote.entered_by);

    // Load quote line items
    // This requires mapping quote items to the form's line item structure
    // For now, we'll show a message that quote is loaded
    alert(`Quote ${quote.quote_number} loaded. Convert to order to proceed.`);
  };

  // Submit quote
  const handleSubmitQuote = async () => {
    setError('');

    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }

    if (!expirationDate) {
      setError('Please select an expiration date');
      return;
    }

    if (!enteredBy) {
      setError('Please select who is entering this quote');
      return;
    }

    const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    if (validLineItems.length === 0) {
      setError('Please add at least one valid feed line item');
      return;
    }

    // Quotes store their own line records, which have no product field, so an
    // add-on cannot survive being quoted. Say so rather than dropping it on
    // the floor and handing over a quote that is quietly missing the oil.
    if (validAddonLines.length > 0) {
      setError('Add-ons cannot be saved on a quote yet — enter them on the order instead');
      return;
    }

    setLoading(true);
    try {
      const quoteData = {
        customer: selectedCustomer.id,
        quote_date: new Date().toISOString().split('T')[0],
        expiration_date: expirationDate,
        shipping_method: shippingMethod,
        is_rush: isRush,
        is_tax_exempt: isTaxExempt,
        special_instructions: specialInstructions,
        status: 'DRAFT',
        entered_by: enteredBy,
        entered_at: new Date().toISOString(),
        items: validLineItems.map((item) => ({
          formula: item.formula!.id,
          is_pelleted: item.isPellet,
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          package_type: apiPackageType(item),
          unit_price_per_lb: item.calculatedPrice?.price_per_lb || 0,
          line_total: item.calculatedPrice?.item_total || 0,
        })),
      };

      await quotesAPI.create(quoteData);
      alert('Quote created successfully!');

      // Reset form
      resetForm();
    } catch (err: any) {
      console.error('Error creating quote:', err);
      setError(err.response?.data?.message || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  // Convert quote to order
  const handleConvertToOrder = async () => {
    if (!loadedQuote) return;

    if (!deliveryDate) {
      setError('Please select a delivery date for the order');
      return;
    }

    setLoading(true);
    try {
      const response = await quotesAPI.convertToOrder(loadedQuote.id, {
        delivery_date: deliveryDate,
      });

      alert(`Quote ${loadedQuote.quote_number} converted to order ${response.data.order.order_number}!`);

      // Reset form
      resetForm();
      setLoadedQuote(null);
    } catch (err: any) {
      console.error('Error converting quote to order:', err);
      setError(err.response?.data?.error || 'Failed to convert quote to order');
    } finally {
      setLoading(false);
    }
  };

  // Reset form helper
  const resetForm = () => {
    setLineItems([
      {
        id: crypto.randomUUID(),
        formula: null,
        selectedGrade: '',
        selectedAnimal: '',
        isPellet: false,
        totalPounds: 0,
        numberOfPackages: 1,
        packageSizeLbs: 0,
        isPromo: false,
        promoReason: '',
      },
    ]);
    setDeliveryDate('');
    setExpirationDate('');
    setShippingMethod('WILL_CALL');
    setIsTaxExempt(false);
    setIsRush(false);
    setLiftgateRequired(null);
    setSpecialInstructions('');
    setMaterialChecks(new Map());
    setAddonLines([]);
    setAddonPrices(new Map());
    setIsEditMode(false);
    setEditingOrderNumber('');
    setEditReason('');
    originalOrderDataRef.current = null;
  };

  const totals = calculateOrderTotals();

  if (loadingOrder) {
    return (
      <div className="order-entry-page">
        <div className="page-header">
          <div>
            <h1>Loading Order...</h1>
            <p className="page-subtitle">Red Bridge Farm Feed Co.</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading order data...</div>
      </div>
    );
  }

  return (
    <div className="order-entry-page">
      <div className="page-header">
        <div>
          <h1>{isEditMode ? `Edit Order #${editingOrderNumber}` : entryType === 'ORDER' ? 'Feed Order Entry' : 'Feed Quote Entry'}</h1>
          <p className="page-subtitle">Red Bridge Farm Feed Co.</p>
        </div>

        {/* Entry Type Toggle - hidden in edit mode */}
        {!isEditMode && (
          <div className="entry-type-toggle">
            <button
              className={`toggle-btn ${entryType === 'ORDER' ? 'active' : ''}`}
              onClick={() => {
                setEntryType('ORDER');
                setLoadedQuote(null);
              }}
            >
              Order
            </button>
            <button
              className={`toggle-btn ${entryType === 'QUOTE' ? 'active' : ''}`}
              onClick={() => setEntryType('QUOTE')}
            >
              Quote
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="order-entry-container">
        {/* LEFT COLUMN - Order Form */}
        <div className="order-form-column">
          <div className="order-form">
            {/* Customer Selection */}
            <div className="form-section">
              <h3 className="section-title">Customer Information</h3>
              <div className="customer-select-row">
                <CustomerSearchSelect
                  customers={customers}
                  selectedCustomer={selectedCustomer}
                  onSelect={setSelectedCustomer}
                />
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCustomerModal(true)}>
                  + New Customer
                </button>
              </div>

              {selectedCustomer && (
                <div className="customer-info">
                  <div className="customer-info-header">
                    <h4>{selectedCustomer.name}</h4>
                    <span className="customer-type-badge">
                      {selectedCustomer.customer_type.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div className="customer-info-section">
                    <strong>Address:</strong>
                    <div className="customer-address">
                      {selectedCustomer.address && <div>{selectedCustomer.address}</div>}
                      <div>
                        {selectedCustomer.city}
                        {selectedCustomer.city && selectedCustomer.state && ', '}
                        {selectedCustomer.state} {selectedCustomer.postal_code}
                      </div>
                    </div>
                  </div>

                  <div className="customer-info-section">
                    {selectedCustomer.phone && (
                      <p>
                        <strong>Phone:</strong> {selectedCustomer.phone}
                      </p>
                    )}
                    {selectedCustomer.email && (
                      <p>
                        <strong>Email:</strong> {selectedCustomer.email}
                      </p>
                    )}
                  </div>

                  {/* View Saved Quotes Button (Quote Mode Only) */}
                  {entryType === 'QUOTE' && savedQuotes.length > 0 && (
                    <div className="customer-info-section">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowQuotesModal(true)}
                      >
                        View Saved Quotes ({savedQuotes.length})
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Shipping Information Panel */}
            {selectedCustomer && shippingProfile && (
              <div className="form-section" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px' }}>
                <h3 className="section-title" style={{ marginTop: 0, fontSize: '1rem', color: '#495057' }}>Shipping Information</h3>

                {/* Delivery Address */}
                {(shippingProfile.shipping_address?.address || shippingProfile.shipping_address?.city) && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', textTransform: 'uppercase', marginBottom: '4px' }}>Delivery Address</div>
                    {shippingProfile.shipping_address.address && <div style={{ fontSize: '0.9rem' }}>{shippingProfile.shipping_address.address}</div>}
                    {(shippingProfile.shipping_address.city || shippingProfile.shipping_address.state) && (
                      <div style={{ fontSize: '0.9rem' }}>
                        {[shippingProfile.shipping_address.city, shippingProfile.shipping_address.state].filter(Boolean).join(', ')}
                        {shippingProfile.shipping_address.zip ? ` ${shippingProfile.shipping_address.zip}` : ''}
                      </div>
                    )}
                    {(shippingProfile.shipping_address.contact_name || shippingProfile.shipping_address.contact_phone) && (
                      <div style={{ fontSize: '0.85rem', color: '#555' }}>
                        Contact: {[shippingProfile.shipping_address.contact_name, shippingProfile.shipping_address.contact_phone].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Carrier & Schedule */}
                {shippingProfile.carrier?.name && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', textTransform: 'uppercase', marginBottom: '4px' }}>Carrier & Schedule</div>
                    <div style={{ fontSize: '0.9rem' }}>
                      <strong>{shippingProfile.carrier.name}</strong>
                      {shippingProfile.carrier.is_direct ? ' (direct)' : ' (handoff)'}
                      {shippingProfile.carrier.transit_days ? ` · Transit: ~${shippingProfile.carrier.transit_days} day${shippingProfile.carrier.transit_days > 1 ? 's' : ''}` : ''}
                    </div>
                    {shippingProfile.carrier.delivery_days && (
                      <div style={{ fontSize: '0.9rem' }}>Delivers: <strong>{shippingProfile.carrier.delivery_days}</strong></div>
                    )}
                    {shippingProfile.carrier.is_limited && (
                      <div style={{ fontSize: '0.85rem', color: '#e65100' }}>⚠ Limited schedule — plan ahead</div>
                    )}
                    {shippingProfile.carrier.requires_early_truck && (
                      <div style={{ fontSize: '0.85rem', color: '#e65100' }}>⚠ Requires early truck</div>
                    )}
                  </div>
                )}

                {/* Site Capabilities */}
                {(shippingProfile.site?.requires_liftgate || shippingProfile.site?.is_residential_or_farm || shippingProfile.site?.has_loading_dock || shippingProfile.site?.requires_appointment) && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', textTransform: 'uppercase', marginBottom: '4px' }}>Site</div>
                    <div style={{ fontSize: '0.9rem' }}>
                      {[
                        shippingProfile.site.requires_liftgate && 'Liftgate required',
                        shippingProfile.site.is_residential_or_farm && 'Residential/farm',
                        shippingProfile.site.has_loading_dock && 'Has loading dock',
                        !shippingProfile.site.has_loading_dock && 'No loading dock',
                        shippingProfile.site.can_receive_53ft ? 'Can receive 53ft trailer' : 'Cannot receive 53ft trailer',
                        shippingProfile.site.requires_appointment && 'Appointment required',
                      ].filter(Boolean).join(' · ')}
                    </div>
                    {(shippingProfile.fees?.liftgate_fee || shippingProfile.fees?.residential_fee || shippingProfile.fees?.remote_fee) && (
                      <div style={{ fontSize: '0.85rem', color: '#e65100' }}>
                        Fees: {[
                          shippingProfile.fees.liftgate_fee && `Liftgate +$${shippingProfile.fees.liftgate_fee}`,
                          shippingProfile.fees.residential_fee && `Residential +$${shippingProfile.fees.residential_fee}`,
                          shippingProfile.fees.remote_fee && `Remote +$${shippingProfile.fees.remote_fee}`,
                        ].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Delivery Notes */}
                {shippingProfile.delivery_notes && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', textTransform: 'uppercase', marginBottom: '4px' }}>Delivery Notes</div>
                    <div style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>{shippingProfile.delivery_notes}</div>
                  </div>
                )}

                {/* Last Shipment */}
                {shippingProfile.last_shipment && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', textTransform: 'uppercase', marginBottom: '4px' }}>Last Shipment</div>
                    <div style={{ fontSize: '0.9rem' }}>
                      {shippingProfile.last_shipment.date && new Date(shippingProfile.last_shipment.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {shippingProfile.last_shipment.carrier_display && ` via ${shippingProfile.last_shipment.carrier_display}`}
                      {shippingProfile.last_shipment.order_number && ` · ${shippingProfile.last_shipment.order_number}`}
                    </div>
                  </div>
                )}

                {/* Milk Run Info */}
                {(shippingProfile.milk_run?.capable || shippingProfile.milk_run?.region) && (
                  <div style={{ marginBottom: '0' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6c757d', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Milk Run{shippingProfile.milk_run.region_display ? `: ${shippingProfile.milk_run.region_display}` : ''}
                    </div>
                    <div style={{ fontSize: '0.9rem' }}>
                      {[
                        shippingProfile.milk_run.equipment_display && `Equipment: ${shippingProfile.milk_run.equipment_display}`,
                        shippingProfile.milk_run.truck_access ? 'Truck access: Yes' : 'No truck access',
                      ].filter(Boolean).join(' · ')}
                    </div>
                    {shippingProfile.milk_run.truck_access_notes && (
                      <div style={{ fontSize: '0.85rem', color: '#555' }}>{shippingProfile.milk_run.truck_access_notes}</div>
                    )}
                    {shippingProfile.milk_run.notes && (
                      <div style={{ fontSize: '0.85rem', color: '#555', fontStyle: 'italic' }}>{shippingProfile.milk_run.notes}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Delivery & Shipping */}
            <div className="form-section">
              <h3 className="section-title">Delivery & Shipping</h3>
              {/* Carrier schedule info banner */}
              {deliveryScheduleInfo && deliveryScheduleInfo.is_limited && entryType === 'ORDER' && (() => {
                const isLTLMethod = SHIPPING_METHODS.find(m => m.value === shippingMethod);
                const isFreight = (isLTLMethod as any)?.isLTL || ['FASTWAY_FREIGHT', 'OAK_HARBOR', 'DIAMOND_LINE', 'ANIMAL_SUPPLY', 'LTL_STORE', 'LTL_FARM'].includes(shippingMethod);
                if (!isFreight) return null;
                return (
                  <div style={{
                    background: deliveryScheduleInfo.is_single_day ? '#fff3e0' : '#e3f2fd',
                    border: `1px solid ${deliveryScheduleInfo.is_single_day ? '#ff9800' : '#2196f3'}`,
                    borderRadius: '6px', padding: '10px 14px', marginBottom: '12px',
                    fontSize: '0.9rem', lineHeight: 1.5,
                  }}>
                    <strong>{deliveryScheduleInfo.carrier}</strong> delivers to{' '}
                    {deliveryScheduleInfo.zone_city}, {deliveryScheduleInfo.zone_state} on{' '}
                    <strong>{deliveryScheduleInfo.delivery_days_text}</strong>
                    {deliveryScheduleInfo.is_single_day ? ' only' : ''}.
                    {deliveryScheduleInfo.is_remote && deliveryScheduleInfo.remote_fee && (
                      <span> Remote fee: ${deliveryScheduleInfo.remote_fee}.</span>
                    )}
                  </div>
                );
              })()}
              <div className="form-row">
                <div className="form-group">
                  <label>{entryType === 'ORDER' ? 'Delivery Date' : 'Expiration Date'} *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={entryType === 'ORDER' ? deliveryDate : expirationDate}
                    onChange={(e) => {
                      if (entryType === 'ORDER') {
                        setDeliveryDate(e.target.value);
                        setDeliveryWarning('');
                        // Validate delivery date
                        if (e.target.value && selectedCustomer) {
                          setDeliveryValidation(null);
                          salesOrdersAPI.validateDelivery({
                            customer_id: selectedCustomer.id,
                            delivery_date: e.target.value,
                            shipping_method: shippingMethod,
                          }).then((res: any) => {
                            setDeliveryValidation(res.data);
                            if (res.data?.warnings?.length > 0) {
                              setDeliveryWarning(res.data.warnings.join(' '));
                            }
                          }).catch(() => {});
                        } else {
                          setDeliveryValidation(null);
                        }
                      } else {
                        setExpirationDate(e.target.value);
                      }
                    }}
                    required
                  />
                  {deliveryWarning && entryType === 'ORDER' && (
                    <div style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '4px', fontWeight: 500 }}>
                      ⚠ {deliveryWarning}
                    </div>
                  )}
                  {/* Ship date + lead time indicator */}
                  {deliveryValidation && entryType === 'ORDER' && deliveryDate && (
                    <div style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                      {deliveryValidation.ship_by_date && (() => {
                        const shipDt = new Date(deliveryValidation.ship_by_date + 'T12:00:00');
                        const delDt = new Date(deliveryDate + 'T12:00:00');
                        const shipLabel = shipDt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        const delLabel = delDt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        const carrierName = shippingProfile?.carrier?.name;
                        const transit = shippingProfile?.carrier?.transit_days;
                        return (
                          <div style={{ color: '#555', marginBottom: '2px' }}>
                            Ships: <strong>{shipLabel}</strong> → Delivers: <strong>{delLabel}</strong>
                            {carrierName && transit ? ` (${transit}-day transit via ${carrierName})` : ''}
                          </div>
                        );
                      })()}
                      {deliveryValidation.lead_time && (() => {
                        const lt = deliveryValidation.lead_time;
                        const colors: Record<string, string> = { green: '#2e7d32', yellow: '#e65100', red: '#c62828' };
                        const icons: Record<string, string> = { green: '✅', yellow: '⚠️', red: '🔴' };
                        return (
                          <div style={{ color: colors[lt.level] || '#555', fontWeight: 500 }}>
                            {icons[lt.level] || ''} {lt.message}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/* Show next valid dates if schedule is limited */}
                  {deliveryScheduleInfo && deliveryScheduleInfo.is_limited && entryType === 'ORDER' && validDeliveryDates.length > 0 && (() => {
                    const isLTLMethod = SHIPPING_METHODS.find(m => m.value === shippingMethod);
                    const isFreight = (isLTLMethod as any)?.isLTL || ['FASTWAY_FREIGHT', 'OAK_HARBOR', 'DIAMOND_LINE', 'ANIMAL_SUPPLY', 'LTL_STORE', 'LTL_FARM'].includes(shippingMethod);
                    if (!isFreight) return null;
                    const nextDates = validDeliveryDates.slice(0, 4);
                    return (
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                        Next valid dates:{' '}
                        {nextDates.map((d, i) => {
                          const dt = new Date(d + 'T12:00:00');
                          const label = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                          return (
                            <span
                              key={d}
                              onClick={() => { setDeliveryDate(d); setDeliveryWarning(''); }}
                              style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline', marginRight: '8px' }}
                            >
                              {label}{i < nextDates.length - 1 ? '' : ''}
                            </span>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label>Shipping Method *</label>
                  <select
                    className="form-select"
                    value={shippingMethod}
                    onChange={(e) => {
                      setShippingMethod(e.target.value);
                      // Re-validate delivery date when shipping method changes
                      if (deliveryDate && selectedCustomer && entryType === 'ORDER') {
                        setDeliveryWarning('');
                        setDeliveryValidation(null);
                        salesOrdersAPI.validateDelivery({
                          customer_id: selectedCustomer.id,
                          delivery_date: deliveryDate,
                          shipping_method: e.target.value,
                        }).then((res: any) => {
                          setDeliveryValidation(res.data);
                          if (res.data?.warnings?.length > 0) {
                            setDeliveryWarning(res.data.warnings.join(' '));
                          }
                        }).catch(() => {});
                      }
                    }}
                  >
                    {SHIPPING_METHODS.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Entered By *</label>
                  <select
                    className="form-select"
                    value={enteredBy || ''}
                    onChange={(e) => setEnteredBy(Number(e.target.value))}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.first_name || employee.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isTaxExempt}
                    onChange={(e) => setIsTaxExempt(e.target.checked)}
                  />
                  Tax Exempt
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isRush}
                    onChange={(e) => setIsRush(e.target.checked)}
                  />
                  Rush Order
                </label>
                {/* Liftgate — required explicit choice on delivery methods (no default);
                    N/A for pickup/store. Radios so "unset" is distinguishable from "no". */}
                {isDeliveryMethod ? (
                  <div className="liftgate-choice" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>Liftgate <span style={{ color: '#dc2626' }}>*</span></span>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input
                        type="radio"
                        name="liftgate"
                        checked={liftgateRequired === true}
                        onChange={() => setLiftgateRequired(true)}
                      />
                      Required (+$25)
                    </label>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input
                        type="radio"
                        name="liftgate"
                        checked={liftgateRequired === false}
                        onChange={() => setLiftgateRequired(false)}
                      />
                      Not needed
                    </label>
                  </div>
                ) : (
                  <span className="checkbox-label" style={{ opacity: 0.5 }}>Liftgate (delivery only)</span>
                )}
              </div>
            </div>

            {/* Feed Line Items */}
            <div className="form-section">
              <div className="section-header-row">
                <h3 className="section-title">Feed Line Items</h3>
                <button className="btn btn-secondary btn-sm" onClick={addLineItem}>
                  + Add Feed Type
                </button>
              </div>

              {lineItems.map((lineItem, index) => (
                <LineItemForm
                  key={lineItem.id}
                  lineItem={lineItem}
                  index={index}
                  feedGrades={feedGrades}
                  onGradeChange={handleGradeChange}
                  onAnimalChange={handleAnimalChange}
                  onFormulaChange={handleFormulaChange}
                  onPelletToggle={handlePelletToggle}
                  onPackagingChange={handlePackagingChange}
                  onTotalPoundsChange={handleTotalPoundsChange}
                  onTotalPoundsBlur={handleTotalPoundsBlur}
                  onNumberOfPackagesChange={handleNumberOfPackagesChange}
                  onNumberOfPackagesBlur={handleNumberOfPackagesBlur}
                  onRemove={removeLineItem}
                  canRemove={lineItems.length > 1}
                  loadAnimalTypes={loadAnimalTypes}
                  loadFormulas={loadFormulas}
                  materialCheck={materialChecks.get(lineItem.id)}
                  onPromoChange={handlePromoChange}
                  onPromoReasonChange={handlePromoReasonChange}
                />
              ))}
            </div>

            {/* Add-Ons: bulk ingredients, canola oil, grit. Not feed
                formulas, so they live in their own section rather than being
                forced through the grade/animal/formula picker. */}
            <div className="form-section">
              <div className="section-header-row">
                <h3 className="section-title">Add-Ons</h3>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={addAddonLine}
                  disabled={addonProducts.length === 0}
                  title={addonProducts.length === 0
                    ? 'No items are marked sellable as add-ons yet'
                    : 'Add a bulk ingredient, oil or grit'}
                >
                  + Add Ingredient
                </button>
              </div>

              {addonProducts.length === 0 && (
                <p className="text-muted" style={{ margin: '4px 0 0' }}>
                  Nothing is marked sellable as an add-on yet. Tick
                  &ldquo;sellable as add-on&rdquo; on a product and set its price.
                </p>
              )}

              {addonLines.map((line) => {
                const product = addonProductById(line.productId);
                const byWeight = product ? product.priced_by_weight : false;
                const lineTotal = addonLineTotal(line);
                return (
                  <div key={line.id} className="line-item-row addon-line">
                    <div className="form-group">
                      <label className="form-label">Item</label>
                      <select
                        className="form-select"
                        value={line.productId ?? ''}
                        onChange={(e) =>
                          handleAddonProductChange(
                            line.id,
                            e.target.value ? parseInt(e.target.value, 10) : null
                          )
                        }
                      >
                        <option value="">Select an ingredient...</option>
                        {addonProducts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — ${p.default_price}/{p.unit || 'each'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {byWeight ? 'Pounds' : 'Quantity'}
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        min={0}
                        step={byWeight ? '0.01' : '1'}
                        value={line.quantity || ''}
                        disabled={!product}
                        onChange={(e) =>
                          handleAddonQuantityChange(
                            line.id,
                            parseFloat(e.target.value) || 0
                          )
                        }
                      />
                      {/* Say the weight out loud: an item add-on carries the
                          catalogue weight into the shipping total, and a blank
                          one deliberately carries none. */}
                      {product && !byWeight && product.addon_weight_lbs && line.quantity > 0 && (
                        <small className="text-muted">
                          {(parseFloat(product.addon_weight_lbs) * line.quantity).toLocaleString()} lbs
                        </small>
                      )}
                      {product && !byWeight && !product.addon_weight_lbs && (
                        <small className="text-muted">No weight on file</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Line Total</label>
                      <div className="line-total">
                        {lineTotal > 0
                          ? `$${lineTotal.toFixed(2)}`
                          : '—'}
                      </div>
                    </div>

                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => removeAddonLine(line.id)}
                      title="Remove this add-on"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Special Instructions */}
            <div className="form-section">
              <h3 className="section-title">Special Instructions</h3>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="Any special delivery or handling instructions..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
              ></textarea>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => {
                if (isEditMode) {
                  navigate(`/orders/${editOrderId}`);
                } else {
                  window.history.back();
                }
              }}>
                Cancel
              </button>

              {/* Edit Mode: Save Changes */}
              {isEditMode && (
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateOrder}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              )}

              {/* Convert to Order Button (when quote is loaded) */}
              {!isEditMode && loadedQuote && entryType === 'QUOTE' && (
                <button
                  className="btn btn-success"
                  onClick={handleConvertToOrder}
                  disabled={loading}
                >
                  {loading ? 'Converting...' : 'Convert to Order'}
                </button>
              )}

              {/* Submit Button (create mode only) */}
              {!isEditMode && !loadedQuote && (
                <button
                  className="btn btn-primary"
                  onClick={entryType === 'ORDER' ? handleSubmitOrder : handleSubmitQuote}
                  disabled={loading}
                >
                  {loading
                    ? entryType === 'ORDER'
                      ? 'Creating Order...'
                      : 'Creating Quote...'
                    : entryType === 'ORDER'
                    ? 'Create Order'
                    : 'Create Quote'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Order Summary */}
        <div className="order-summary-column">
          <OrderSummary
            selectedCustomer={selectedCustomer}
            lineItems={lineItems}
            totals={totals}
            isTaxExempt={isTaxExempt}
            shippingMethod={shippingMethod}
            materialChecks={materialChecks}
            employees={employees}
            enteredBy={enteredBy}
            deliveryDate={deliveryDate}
            specialInstructions={specialInstructions}
            isRush={isRush}
            entryType={entryType}
            expirationDate={expirationDate}
            palletDiscount={palletDiscount}
          />
        </div>
      </div>

      {/* Customer Creation Modal */}
      <CustomerCreationModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerCreated={(newCustomer) => {
          loadCustomers();
          setSelectedCustomer(newCustomer);
          setShowCustomerModal(false);
        }}
      />

      {/* Saved Quotes Modal */}
      {showQuotesModal && (
        <div className="modal-overlay" onClick={() => setShowQuotesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Saved Quotes for {selectedCustomer?.name}</h2>
              <button className="modal-close" onClick={() => setShowQuotesModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {savedQuotes.length === 0 ? (
                <p>No saved quotes found for this customer.</p>
              ) : (
                <table className="quotes-table">
                  <thead>
                    <tr>
                      <th>Quote #</th>
                      <th>Date</th>
                      <th>Expires</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedQuotes.map((quote) => (
                      <tr key={quote.id}>
                        <td>{quote.quote_number}</td>
                        <td>{new Date(quote.quote_date).toLocaleDateString()}</td>
                        <td>{new Date(quote.expiration_date).toLocaleDateString()}</td>
                        <td>${quote.total_amount.toFixed(2)}</td>
                        <td>
                          <span className={`status-badge status-${quote.status.toLowerCase()}`}>
                            {quote.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => loadQuote(quote)}
                          >
                            Load
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowQuotesModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reason Modal */}
      {showEditReasonModal && (
        <div className="modal-overlay" onClick={() => setShowEditReasonModal(false)}>
          <div className="modal-content edit-reason-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Reason Required</h2>
              <button className="modal-close" onClick={() => setShowEditReasonModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p className="edit-reason-description">
                Please describe what changed and why for audit tracking.
              </p>
              <textarea
                className="form-textarea edit-reason-textarea"
                rows={4}
                placeholder="e.g., Customer requested additional 40# bags of All Flock pellet..."
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditReasonModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submitOrderUpdate}
                disabled={!editReason.trim()}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Searchable Customer Dropdown Component
interface CustomerSearchSelectProps {
  customers: Customer[];
  selectedCustomer: Customer | null;
  onSelect: (customer: Customer | null) => void;
}

const CustomerSearchSelect: React.FC<CustomerSearchSelectProps> = ({
  customers,
  selectedCustomer,
  onSelect,
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    const terms = search.toLowerCase().split(/\s+/);
    return customers.filter((c) => {
      const haystack = `${c.name} ${c.contact_person || ''} ${c.city || ''} ${c.state || ''} ${c.customer_type || ''}`.toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
  }, [customers, search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightIndex]) {
        (items[highlightIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      setHighlightIndex(0);
      e.preventDefault();
      return;
    }
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && filtered[highlightIndex]) {
          selectCustomer(filtered[highlightIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightIndex(-1);
        break;
    }
  };

  const selectCustomer = (customer: Customer) => {
    onSelect(customer);
    setSearch('');
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const clearSelection = () => {
    onSelect(null);
    setSearch('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const formatCustomerType = (type: string) => {
    const labels: Record<string, string> = {
      LOCAL_WILL_CALL: 'Local',
      WHOLESALE_FARM: 'Wholesale',
      RETAIL_PARTNER: 'Retail',
      CJ_CUSTOMER: 'CJ',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  if (selectedCustomer) {
    return (
      <div className="customer-search-selected" ref={containerRef}>
        <div className="customer-search-selected-value">
          <span className="selected-customer-name">{selectedCustomer.name}</span>
          {selectedCustomer.contact_person && (
            <span className="selected-customer-contact">({selectedCustomer.contact_person})</span>
          )}
          <span className="selected-customer-location">
            {[selectedCustomer.city, selectedCustomer.state].filter(Boolean).join(', ')}
          </span>
        </div>
        <button
          type="button"
          className="customer-search-clear"
          onClick={clearSelection}
          title="Clear selection"
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div className="customer-search-container" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        className="form-input customer-search-input"
        placeholder="Search customers by name, contact, or city..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
          setHighlightIndex(0);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {isOpen && (
        <div className="customer-search-dropdown" ref={listRef}>
          {filtered.length === 0 ? (
            <div className="customer-search-empty">
              No customers match &ldquo;{search}&rdquo;
            </div>
          ) : (
            filtered.slice(0, 50).map((customer, idx) => (
              <div
                key={customer.id}
                className={`customer-search-option${idx === highlightIndex ? ' highlighted' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectCustomer(customer);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                <span className="customer-option-name">{customer.name}</span>
                {customer.contact_person && (
                  <span className="customer-option-contact">({customer.contact_person})</span>
                )}
                <span className="customer-option-meta">
                  {[customer.city, customer.state].filter(Boolean).join(', ')}
                  {customer.customer_type && (
                    <span className="customer-option-type">{formatCustomerType(customer.customer_type)}</span>
                  )}
                </span>
              </div>
            ))
          )}
          {filtered.length > 50 && (
            <div className="customer-search-more">
              {filtered.length - 50} more — type to narrow results
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Line Item Form Component
interface LineItemFormProps {
  lineItem: OrderLineItem;
  index: number;
  feedGrades: Array<{ value: string; label: string }>;
  onGradeChange: (id: string, grade: string) => void;
  onAnimalChange: (id: string, animal: string) => void;
  onFormulaChange: (id: string, formulaId: number, formulas: FeedFormula[]) => void;
  onPelletToggle: (id: string, isPellet: boolean) => void;
  onPackagingChange: (id: string, packaging: Packaging) => void;
  onTotalPoundsChange: (id: string, totalPounds: number) => void;
  onTotalPoundsBlur: (id: string) => void;
  onNumberOfPackagesChange: (id: string, numberOfPackages: number) => void;
  onNumberOfPackagesBlur: (id: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  loadAnimalTypes: (id: string, grade: string) => Promise<Array<{ value: string; label: string }>>;
  loadFormulas: (id: string, grade: string, animal: string) => Promise<FeedFormula[]>;
  materialCheck?: MaterialCheck;
  onPromoChange: (id: string, isPromo: boolean) => void;
  onPromoReasonChange: (id: string, reason: string) => void;
}

const LineItemForm: React.FC<LineItemFormProps> = ({
  lineItem,
  index,
  feedGrades,
  onGradeChange,
  onAnimalChange,
  onFormulaChange,
  onPelletToggle,
  onPackagingChange,
  onTotalPoundsChange,
  onTotalPoundsBlur,
  onNumberOfPackagesChange,
  onNumberOfPackagesBlur,
  onRemove,
  canRemove,
  loadAnimalTypes,
  loadFormulas,
  materialCheck,
  onPromoChange,
  onPromoReasonChange,
}) => {
  const [animalTypes, setAnimalTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);

  // Load animal types when grade changes
  useEffect(() => {
    if (lineItem.selectedGrade) {
      loadAnimalTypes(lineItem.id, lineItem.selectedGrade).then(setAnimalTypes);
    } else {
      setAnimalTypes([]);
    }
  }, [lineItem.selectedGrade]);

  // Load formulas when animal changes
  useEffect(() => {
    if (lineItem.selectedGrade && lineItem.selectedAnimal) {
      loadFormulas(lineItem.id, lineItem.selectedGrade, lineItem.selectedAnimal).then(setFormulas);
    } else {
      setFormulas([]);
    }
  }, [lineItem.selectedGrade, lineItem.selectedAnimal]);

  // Check available forms from the formula
  const availableForms = lineItem.formula?.available_forms || [];
  const isMashAvailable = availableForms.includes('MASH');
  const isPelletAvailable = availableForms.includes('PELLET');

  return (
    <div className="line-item-card">
      <div className="line-item-header">
        <h4>Feed Type #{index + 1}</h4>
        {canRemove && (
          <button className="btn-remove" onClick={() => onRemove(lineItem.id)}>
            ✕ Remove
          </button>
        )}
      </div>

      {/* Cascading Filters */}
      <div className="form-row">
        <div className="form-group">
          <label>Feed Grade *</label>
          <select
            className="form-select"
            value={lineItem.selectedGrade}
            onChange={(e) => onGradeChange(lineItem.id, e.target.value)}
          >
            <option value="">Select Grade...</option>
            {feedGrades.map((grade) => (
              <option key={grade.value} value={grade.value}>
                {grade.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Animal Type *</label>
          <select
            className="form-select"
            value={lineItem.selectedAnimal}
            onChange={(e) => onAnimalChange(lineItem.id, e.target.value)}
            disabled={!lineItem.selectedGrade}
          >
            <option value="">Select Animal...</option>
            {animalTypes.map((animal) => (
              <option key={animal.value} value={animal.value}>
                {animal.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Formula *</label>
        <select
          className="form-select"
          value={lineItem.formula?.id || ''}
          onChange={(e) => onFormulaChange(lineItem.id, Number(e.target.value), formulas)}
          disabled={!lineItem.selectedAnimal}
        >
          <option value="">Select Formula...</option>
          {formulas.map((formula) => (
            <option key={formula.id} value={formula.id}>
              {formula.name}
            </option>
          ))}
        </select>
      </div>

      {/* Mash/Pellet Toggle */}
      {lineItem.formula && (
        <>
          <div className="form-group">
            <label>Form</label>
            <div className="radio-group">
              <label className={`radio-label${!lineItem.isPellet ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name={`form-${lineItem.id}`}
                  checked={!lineItem.isPellet}
                  onChange={() => onPelletToggle(lineItem.id, false)}
                  disabled={!isMashAvailable}
                />
                Mash {!isMashAvailable && '(Not available)'}
              </label>
              <label className={`radio-label${lineItem.isPellet ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name={`form-${lineItem.id}`}
                  checked={lineItem.isPellet}
                  onChange={() => onPelletToggle(lineItem.id, true)}
                  disabled={!isPelletAvailable}
                />
                Pellet {!isPelletAvailable && '(Not available)'}
              </label>
            </div>
          </div>

          {/* Packaging: bags or bulk. The bag size is the feed's (FeedFormula.bag_size). */}
          <div className="form-group">
            <label>Packaging</label>
            <div className="radio-group">
              <label className={`radio-label${packagingOf(lineItem) === 'bags' ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name={`packaging-${lineItem.id}`}
                  checked={packagingOf(lineItem) === 'bags'}
                  onChange={() => onPackagingChange(lineItem.id, 'bags')}
                />
                Bags{lineItem.formula ? ` (${bagWord(bagSizeOf(lineItem.formula))}s)` : ''}
              </label>
              <label className={`radio-label${packagingOf(lineItem) === 'bulk' ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name={`packaging-${lineItem.id}`}
                  checked={packagingOf(lineItem) === 'bulk'}
                  onChange={() => onPackagingChange(lineItem.id, 'bulk')}
                />
                Bulk (super sacks / totes)
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{packagingOf(lineItem) === 'bags' ? 'How many bags? *' : 'Total Pounds *'}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-input"
                placeholder={packagingOf(lineItem) === 'bags' ? 'e.g. 65' : 'e.g. 2600'}
                value={packagingOf(lineItem) === 'bags' ? (lineItem.numberOfPackages || '') : (lineItem.totalPounds || '')}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty or numeric values only - NO calculations while typing
                  if (value === '' || /^\d+$/.test(value)) {
                    if (packagingOf(lineItem) === 'bags') {
                      onNumberOfPackagesChange(lineItem.id, value === '' ? 0 : Number(value));
                    } else {
                      onTotalPoundsChange(lineItem.id, value === '' ? 0 : Number(value));
                    }
                  }
                }}
                onBlur={() => (packagingOf(lineItem) === 'bags' ? onNumberOfPackagesBlur(lineItem.id) : onTotalPoundsBlur(lineItem.id))}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="form-group">
              <label>{packagingOf(lineItem) === 'bags' ? 'Total Pounds' : 'How many super sacks? *'}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-input"
                placeholder={packagingOf(lineItem) === 'bags' ? 'or enter a weight' : '1'}
                value={packagingOf(lineItem) === 'bags' ? (lineItem.totalPounds || '') : (lineItem.numberOfPackages || '')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    if (packagingOf(lineItem) === 'bags') {
                      onTotalPoundsChange(lineItem.id, value === '' ? 0 : Number(value));
                    } else {
                      onNumberOfPackagesChange(lineItem.id, value === '' ? 1 : Math.max(1, Number(value)));
                    }
                  }
                }}
                onBlur={() => (packagingOf(lineItem) === 'bags' ? onTotalPoundsBlur(lineItem.id) : onNumberOfPackagesBlur(lineItem.id))}
                onFocus={(e) => e.target.select()}
              />
            </div>
          </div>

          {/* What the line is, in the mill's words - the numbers the order will carry. */}
          <div className="form-group">
            <label>Packaging summary</label>
            <div className="calculated-value">
              {lineItem.totalPounds > 0 && lineItem.numberOfPackages > 0
                ? (packagingOf(lineItem) === 'bags'
                  ? `${lineItem.numberOfPackages} × ${bagWord(bagSizeOf(lineItem.formula))}${lineItem.numberOfPackages === 1 ? '' : 's'} = ${lineItem.totalPounds.toLocaleString()} lb · priced per bag`
                  : `${lineItem.numberOfPackages} × ${lineItem.packageSizeLbs.toLocaleString()} lb super sack${lineItem.numberOfPackages === 1 ? '' : 's'} = ${lineItem.totalPounds.toLocaleString()} lb · priced per pound`)
                : (packagingOf(lineItem) === 'bags' ? 'Enter how many bags (or a weight)' : 'Enter the weight and how many super sacks')}
            </div>
          </div>

          {/* Soft, non-blocking oversize warning: a single package heavier than one
              pallet is almost always a quantity mis-key (e.g. 3,400 typed as 1 unit
              instead of 4 × 850). Skipped for explicitly-typed BULK/BYOC/supersack
              lines (edit of a genuinely-large line); no legit package exceeds a pallet.
              Guards divide-by-zero (packages 0/blank → no warning). */}
          {(() => {
            const pkgs = lineItem.numberOfPackages;
            const perUnit = pkgs > 0 ? lineItem.totalPounds / pkgs : 0;
            const pt = (lineItem.packageType || '').toUpperCase();
            const explicitLarge = packagingOf(lineItem) === 'bulk' || pt === 'BULK' || pt === 'BYOC' || pt.startsWith('SUPERSACK');
            if (!(pkgs > 0) || perUnit <= PALLET_MAX_LBS || explicitLarge) return null;
            // Suggested under-a-pallet split of the entered TOTAL.
            const n = Math.ceil(lineItem.totalPounds / PALLET_MAX_LBS);
            const each = Math.round(lineItem.totalPounds / n);
            return (
              <div style={{ color: '#b45309', fontSize: '0.85rem', marginTop: '4px', fontWeight: 500 }}>
                ⚠ Per-unit weight is {Math.round(perUnit).toLocaleString()} lb — over one pallet
                ({PALLET_MAX_LBS.toLocaleString()} lb). Did you mean to set the quantity?
                {n > 1 && ` e.g. ${lineItem.totalPounds.toLocaleString()} lb = ${n} × ${each.toLocaleString()} lb.`}
              </div>
            );
          })()}

          {/* FIX 2: Material Check (NO ingredient snapshot, just availability) */}
          {materialCheck && (
            <div className={`material-status ${materialCheck.all_materials_available ? 'available' : 'unavailable'}`}>
              <strong>Materials:</strong>{' '}
              {materialCheck.all_materials_available ? '✓ Available' : '✗ Some materials unavailable'}
            </div>
          )}

          {/* Promo / No Cost */}
          <div className={`promo-row${lineItem.isPromo ? ' promo-active' : ''}`}>
            <label className="checkbox-label promo-checkbox">
              <input
                type="checkbox"
                checked={lineItem.isPromo}
                onChange={(e) => onPromoChange(lineItem.id, e.target.checked)}
              />
              Promo (No Cost)
            </label>
            {lineItem.isPromo && (
              <input
                type="text"
                className="promo-reason-input"
                placeholder="Promo reason (required)"
                value={lineItem.promoReason}
                onChange={(e) => onPromoReasonChange(lineItem.id, e.target.value)}
              />
            )}
          </div>

          {/* Promo Price Display */}
          {lineItem.isPromo && lineItem.calculatedPrice && (
            <div className="promo-price-display">
              <span className="promo-original">${lineItem.calculatedPrice.item_total.toFixed(2)}</span>{' '}
              <strong>$0.00</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Order Summary Component
interface OrderSummaryProps {
  selectedCustomer: Customer | null;
  lineItems: OrderLineItem[];
  totals: {
    totalWeight: number;
    subtotal: number;
    taxRate: number;
    taxAmount: number;
    total: number;
    estimatedPallets: number;
  };
  isTaxExempt: boolean;
  shippingMethod: string;
  materialChecks: Map<string, MaterialCheck>;
  employees: User[];
  enteredBy: number | null;
  deliveryDate: string;
  specialInstructions: string;
  isRush: boolean;
  entryType?: 'ORDER' | 'QUOTE';
  expirationDate?: string;
  palletDiscount?: { pallets: number; per_bag: number; bags: number; savings: number } | null;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  selectedCustomer,
  lineItems,
  totals,
  isTaxExempt,
  shippingMethod,
  materialChecks,
  employees,
  enteredBy,
  deliveryDate,
  specialInstructions,
  isRush,
  entryType = 'ORDER',
  expirationDate = '',
  palletDiscount = null,
}) => {
  const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);

  // An add-ons-only order (a drum of oil, no feed) still has totals worth
  // showing, so this checks the money rather than the feed lines alone.
  if (!selectedCustomer || (validLineItems.length === 0 && totals.subtotal <= 0)) {
    return (
      <div className="order-summary-card">
        <h3 className="summary-title">Order Summary</h3>
        <div className="summary-empty">Select customer and add feed items to see order summary</div>
      </div>
    );
  }

  // Generate email content
  const generateEmailContent = () => {
    const isOrder = entryType === 'ORDER';
    const documentType = isOrder ? 'ORDER' : 'QUOTE';
    const dateField = isOrder ? deliveryDate : expirationDate;
    const dateLabel = isOrder ? 'Delivery Date' : 'Expiration Date';

    const enteredByName = enteredBy
      ? employees.find((e) => e.id === enteredBy)?.first_name ||
        employees.find((e) => e.id === enteredBy)?.username ||
        'Unknown'
      : 'Not specified';

    let body = `RED BRIDGE FARM FEED CO.
145 W 3rd Ave
Kettle Falls, WA 99141
509-738-9004
www.theredbridgefarm.com

${documentType} CONFIRMATION
─────────────────────────────────────────

${documentType} Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
${dateLabel}: ${dateField ? new Date(dateField).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Not specified'}
${isRush ? '⚡ RUSH ORDER\n' : ''}
Customer: ${selectedCustomer.name}
Location: ${selectedCustomer.city}, ${selectedCustomer.state}
Shipping Method: ${SHIPPING_METHODS.find((m) => m.value === shippingMethod)?.label}
Entered By: ${enteredByName}

─────────────────────────────────────────
${documentType} DETAILS
─────────────────────────────────────────

`;

    // Add line items
    validLineItems.forEach((item, index) => {
      body += `${index + 1}. ${item.formula!.name}
   Grade: ${item.formula!.feed_grade} | Animal: ${item.formula!.animal_type}
   Form: ${item.isPellet ? 'Pellet' : 'Mash'}
   Quantity: ${item.totalPounds} lbs (${item.numberOfPackages} packages × ${item.packageSizeLbs} lbs)
`;

      if (item.calculatedPrice) {
        const priceDisplay = (item.calculatedPrice.pricing_display_method === 'PER_PACKAGE' && item.calculatedPrice.price_per_package)
          ? `$${item.calculatedPrice.price_per_package.toFixed(2)}/bag × ${item.numberOfPackages} bags`
          : `$${(item.calculatedPrice.price_per_lb || 0).toFixed(4)}/lb × ${item.totalPounds} lbs`;

        body += `   Price: ${priceDisplay} = $${item.calculatedPrice.item_total.toFixed(2)}
`;
      }
      body += '\n';
    });

    // Add totals
    body += `─────────────────────────────────────────
SUMMARY
─────────────────────────────────────────

Total Weight: ${totals.totalWeight.toLocaleString()} lbs
Estimated Pallets: ${totals.estimatedPallets} (2600 lb max/pallet)
Subtotal: $${totals.subtotal.toFixed(2)}
Tax (${isTaxExempt ? 'Exempt' : `${totals.taxRate}%`}): $${totals.taxAmount.toFixed(2)}
TOTAL: $${totals.total.toFixed(2)}
`;

    if (specialInstructions) {
      body += `
─────────────────────────────────────────
SPECIAL INSTRUCTIONS
─────────────────────────────────────────
${specialInstructions}
`;
    }

    body += `
─────────────────────────────────────────
Thank you for your ${isOrder ? 'order' : 'quote request'}!
Non-GMO, Soy & Corn-Free Feeds
Healthy Feed. Healthy Food. Healthy Families.
`;

    return body;
  };

  // Handle email button click
  const handleEmailClick = () => {
    const isOrder = entryType === 'ORDER';
    const subject = isOrder
      ? `Feed Order for ${selectedCustomer.name}`
      : `Feed Quote for ${selectedCustomer.name}`;

    const body = generateEmailContent();

    // Construct mailto URL
    const mailtoUrl = `mailto:${selectedCustomer.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Open email client
    window.location.href = mailtoUrl;
  };

  return (
    <div className="order-summary-card">
      <h3 className="summary-title">Order Summary</h3>

      <div className="summary-section">
        <p>
          <strong>Customer:</strong> {selectedCustomer.name}
        </p>
        <p>
          <strong>Shipping:</strong> {SHIPPING_METHODS.find((m) => m.value === shippingMethod)?.label}
        </p>
        {enteredBy && (
          <p>
            <strong>Entered By:</strong> {employees.find((e) => e.id === enteredBy)?.first_name || employees.find((e) => e.id === enteredBy)?.username || 'Unknown'}
          </p>
        )}
      </div>

      <div className="summary-divider"></div>

      <h4 className="materials-title">Feed Items ({validLineItems.length})</h4>
      <div className="materials-list">
        {validLineItems.map((item, index) => (
          <div key={item.id} className="material-item">
            <div className="material-name">
              {index + 1}. {item.formula!.name}
            </div>
            <div className="material-details">
              <p>
                Grade: {item.formula!.feed_grade} | Animal: {item.formula!.animal_type}
              </p>
              <p>
                {item.isPellet ? 'Pellet' : 'Mash'} | {item.totalPounds} lbs
              </p>
              <p>
                {item.numberOfPackages} packages × {item.packageSizeLbs} lbs
              </p>
              {item.isPromo ? (
                <p>
                  <strong>
                    <span className="promo-original">${item.calculatedPrice?.item_total.toFixed(2) || '0.00'}</span>{' '}
                    $0.00 — PROMO
                  </strong>
                  {item.promoReason && <br />}
                  {item.promoReason && <span style={{ fontSize: '0.8rem', color: '#92400e' }}>{item.promoReason}</span>}
                </p>
              ) : item.calculatedPrice && (
                <p>
                  {item.calculatedPrice.pricing_display_method === 'PER_PACKAGE' && item.calculatedPrice.price_per_package ? (
                    <strong>Price: ${item.calculatedPrice.price_per_package.toFixed(2)}/bag × {item.numberOfPackages} bags = ${item.calculatedPrice.item_total.toFixed(2)}</strong>
                  ) : (
                    <strong>Price: ${(item.calculatedPrice.price_per_lb || 0).toFixed(4)}/lb × {item.totalPounds.toLocaleString()} lbs = ${item.calculatedPrice.item_total.toFixed(2)}</strong>
                  )}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="summary-divider"></div>

      <div className="summary-pricing">
        <div className="price-row">
          <span>Total Weight:</span>
          <span>{totals.totalWeight.toFixed(0)} lbs</span>
        </div>
        <div className="price-row">
          <span>Estimated Pallets:</span>
          <span>{totals.estimatedPallets} (2600 lb max/pallet)</span>
        </div>
        {palletDiscount && palletDiscount.savings > 0 && (
          <div className="price-row" style={{ color: '#166534' }}>
            <span>Pallet savings ({palletDiscount.pallets} pallets, ${palletDiscount.per_bag.toFixed(2)} off each bag):</span>
            <span>-${palletDiscount.savings.toFixed(2)}</span>
          </div>
        )}
        <div className="price-row">
          <span>Subtotal:</span>
          <span>${totals.subtotal.toFixed(2)}</span>
        </div>
        <div className="price-row">
          <span>Tax ({isTaxExempt ? 'Exempt' : `${totals.taxRate}%`}):</span>
          <span>${totals.taxAmount.toFixed(2)}</span>
        </div>
        <div className="price-row total">
          <span>Total:</span>
          <span>${totals.total.toFixed(2)}</span>
        </div>
      </div>

      {/* Material Warnings */}
      {Array.from(materialChecks.values()).some((check) => !check.all_materials_available) && (
        <div className="materials-warning">⚠ Some materials unavailable - Check line items</div>
      )}

      {/* Action Buttons */}
      <div className="summary-actions no-print">
        <button onClick={handleEmailClick} className="email-order-btn">
          📧 Email {entryType === 'ORDER' ? 'Order' : 'Quote'}
        </button>
        <button onClick={() => window.print()} className="print-order-btn">
          🖨️ Print {entryType === 'ORDER' ? 'Order' : 'Quote'}
        </button>
      </div>

      {/* Print-Only Layout */}
      <div className="print-only-order">
        <div className="print-header">
          <div className="company-info">
            <h1>RED BRIDGE FARM FEED CO.</h1>
            <p>145 W 3rd Ave</p>
            <p>Kettle Falls, WA 99141</p>
            <p>509-738-9004</p>
            <p>www.theredbridgefarm.com</p>
          </div>
        </div>

        <div className="print-divider"></div>

        <h2 className="print-title">ORDER CONFIRMATION</h2>
        <div className="print-order-info">
          <p><strong>Order Date:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          <p><strong>Delivery Date:</strong> {deliveryDate ? new Date(deliveryDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '-'}</p>
          {isRush && <p className="rush-indicator"><strong>⚡ RUSH ORDER</strong></p>}
        </div>

        <div className="print-divider"></div>

        <div className="print-customer-section">
          <div className="print-bill-to">
            <h3>BILL TO:</h3>
            <p><strong>{selectedCustomer.name}</strong></p>
            <p>{selectedCustomer.city}, {selectedCustomer.state}</p>
          </div>
          <div className="print-ship-to">
            <h3>SHIP TO:</h3>
            <p><strong>Shipping Method:</strong> {SHIPPING_METHODS.find((m) => m.value === shippingMethod)?.label}</p>
          </div>
        </div>

        <div className="print-divider"></div>

        <h3>ORDER DETAILS</h3>
        <table className="print-order-table">
          <thead>
            <tr>
              <th>Item</th>
              <th className="text-right">Quantity</th>
              <th className="text-right">Price</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {validLineItems.map((item, index) => (
              <tr key={index}>
                <td>
                  <div className="print-item-name">{item.formula!.name}</div>
                  <div className="print-item-details">
                    {item.formula!.feed_grade} | {item.formula!.animal_type}
                  </div>
                  <div className="print-item-details">
                    {item.isPellet ? 'Pellet' : 'Mash'} | {item.numberOfPackages} packages × {item.packageSizeLbs} lbs
                  </div>
                </td>
                <td className="text-right">{item.totalPounds.toLocaleString()} lbs</td>
                <td className="text-right">
                  {item.calculatedPrice && (
                    item.calculatedPrice.pricing_display_method === 'PER_PACKAGE' && item.calculatedPrice.price_per_package
                      ? `$${item.calculatedPrice.price_per_package.toFixed(2)}/bag`
                      : `$${(item.calculatedPrice.price_per_lb || 0).toFixed(4)}/lb`
                  )}
                </td>
                <td className="text-right">${item.calculatedPrice?.item_total.toFixed(2) || '0.00'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="print-divider"></div>

        <div className="print-totals">
          <div className="print-total-row">
            <span>Total Weight:</span>
            <span>{totals.totalWeight.toLocaleString()} lbs</span>
          </div>
          <div className="print-total-row">
            <span>Estimated Pallets:</span>
            <span>{totals.estimatedPallets}</span>
          </div>
          <div className="print-total-row">
            <span>Subtotal:</span>
            <span>${totals.subtotal.toFixed(2)}</span>
          </div>
          <div className="print-total-row">
            <span>Tax ({isTaxExempt ? 'Exempt' : `${totals.taxRate}%`}):</span>
            <span>${totals.taxAmount.toFixed(2)}</span>
          </div>
          <div className="print-total-row print-final-total">
            <span><strong>TOTAL:</strong></span>
            <span><strong>${totals.total.toFixed(2)}</strong></span>
          </div>
          <div className="print-total-row">
            <span>Tax Status:</span>
            <span>{isTaxExempt ? 'Tax Exempt' : 'Taxable'}</span>
          </div>
        </div>

        {specialInstructions && (
          <>
            <div className="print-divider"></div>
            <div className="print-special-instructions">
              <h3>Special Instructions:</h3>
              <p>{specialInstructions}</p>
            </div>
          </>
        )}

        {enteredBy && (
          <div className="print-entered-by">
            <p><em>Entered by: {employees.find((e) => e.id === enteredBy)?.first_name || 'Unknown'}</em></p>
          </div>
        )}

        <div className="print-divider"></div>

        <div className="print-footer">
          <p><strong>Thank you for your order!</strong></p>
          <p>Non-GMO, Soy & Corn-Free Feeds</p>
          <p><em>Healthy Feed. Healthy Food. Healthy Families.</em></p>
        </div>
      </div>
    </div>
  );
};

export default OrderEntry;
